const normalizeFulfillmentCacheValue = (value) => {
  if (Array.isArray(value)) {
    return value.map(normalizeFulfillmentCacheValue)
  }

  if (value && typeof value === 'object') {
    return Object.keys(value)
      .sort()
      .reduce((accumulator, key) => {
        accumulator[key] = normalizeFulfillmentCacheValue(value[key])
        return accumulator
      }, {})
  }

  return value
}

export const buildCampaignFulfillmentCacheKey = (queryParams = {}) => {
  const { page_number, ...baseQueryParams } = queryParams || {}
  return JSON.stringify(normalizeFulfillmentCacheValue(baseQueryParams))
}

export const buildApiTimingsCacheKey = (queryParams = {}) => {
  const { page_number, page_size, ...baseQueryParams } = queryParams || {}
  return JSON.stringify(normalizeFulfillmentCacheValue(baseQueryParams))
}

const getUniqueCampaignFulfillmentRows = (rows = []) => {
  const uniqueRows = []

  rows.forEach((item) => {
    const existingIndex = uniqueRows.findIndex((existingItem) => existingItem.id == item.id)

    if (existingIndex <= -1) {
      uniqueRows.push(item)
    }
  })

  return uniqueRows
}

export const handleGetCampaignFulfillment = (state, action, dataKey, options = {}) => {
  const {
    loadingKey = 'loading',
    errorKey = 'error',
    buildCacheKey = buildCampaignFulfillmentCacheKey,
  } = options
  const queryParams = action.payload?.queryParams || action.payload || {}
  const pageNumber = parseInt(queryParams.page_number, 10) || 1
  const useCache = Boolean(action.payload?.useCache)
  const appendResults = Boolean(action.payload?.appendResults)
  const cacheKey = useCache ? buildCacheKey(queryParams) : null
  const shouldResetCache =
    useCache &&
    pageNumber === 1 &&
    !appendResults &&
    state.active_cache_key &&
    state.active_cache_key !== cacheKey
  const cachedPage = useCache ? state.page_cache?.[cacheKey]?.[pageNumber] : null

  return {
    ...state,
    ...(errorKey ? { [errorKey]: null } : {}),
    [loadingKey]: !cachedPage,
    active_cache_key: useCache ? cacheKey : null,
    [dataKey]: cachedPage?.rows || ((pageNumber === 1 || !appendResults) ? [] : state[dataKey]),
    last_page_fetched: cachedPage?.page_number || state.last_page_fetched,
    has_next_page: typeof cachedPage?.has_next_page === 'boolean' ? cachedPage.has_next_page : state.has_next_page,
    has_lookahead_pages: typeof cachedPage?.has_lookahead_pages === 'boolean'
      ? cachedPage.has_lookahead_pages
      : state.has_lookahead_pages,
    total_number_of_pages: Number.isFinite(Number(cachedPage?.total_number_of_pages))
      ? Number(cachedPage.total_number_of_pages)
      : state.total_number_of_pages,
    page_cache: shouldResetCache ? {} : state.page_cache,
  }
}

export const handleGetCampaignFulfillmentSuccess = (state, action, dataKey, options = {}) => {
  const {
    loadingKey = 'loading',
    errorKey = 'error',
    buildCacheKey = buildCampaignFulfillmentCacheKey,
  } = options
  const rows = Array.isArray(action.payload?.data) ? action.payload.data : []
  const additional = action.payload?.additional_data || {}
  const queryParams = action.meta?.queryParams || {}
  const appendResults = Boolean(action.meta?.appendResults)
  const useCache = Boolean(action.meta?.useCache)
  const pageNumber = parseInt(additional.page_number, 10)
  const safePage = Number.isFinite(pageNumber) && pageNumber > 0 ? pageNumber : 1
  const hasNextPage = typeof additional.has_next_page === 'boolean' ? additional.has_next_page : false
  const hasLookAheadPages = typeof additional.has_lookahead_pages === 'boolean' ? additional.has_lookahead_pages : false
  const totalNumberOfPages = Number.isFinite(Number(additional.total_number_of_pages))
    ? Number(additional.total_number_of_pages)
    : 0
  const cacheKey = useCache ? buildCacheKey(queryParams) : null
  const pageSnapshot = {
    rows,
    page_number: safePage,
    has_next_page: hasNextPage,
    has_lookahead_pages: hasLookAheadPages,
    total_number_of_pages: totalNumberOfPages,
  }

  return {
    ...state,
    ...(errorKey ? { [errorKey]: null } : {}),
    [loadingKey]: false,
    active_cache_key: useCache ? cacheKey : state.active_cache_key,
    [dataKey]: appendResults
      ? getUniqueCampaignFulfillmentRows([...state[dataKey], ...rows])
      : rows,
    last_fetched_at: new Date(),
    last_page_fetched: safePage,
    has_next_page: hasNextPage,
    has_lookahead_pages: hasLookAheadPages,
    total_number_of_pages: totalNumberOfPages,
    page_cache: useCache ? {
      ...state.page_cache,
      [cacheKey]: {
        ...(state.page_cache?.[cacheKey] || {}),
        [safePage]: pageSnapshot,
      },
    } : state.page_cache,
  }
}

export const handleGetCampaignFulfillmentError = (state, action, dataKey, options = {}) => {
  const {
    loadingKey = 'loading',
    errorKey = 'error',
  } = options

  return {
    ...state,
    ...(errorKey ? { [errorKey]: action.payload } : {}),
    [dataKey]: [],
    [loadingKey]: false,
  }
}

export const API_TIMINGS_PAGINATION_OPTIONS = {
  loadingKey: 'api_timings_data_loading',
  errorKey: 'api_timings_data_error',
  buildCacheKey: buildApiTimingsCacheKey,
}
